import {
  ArrowDownIcon,
  BackwardIcon,
  CheckIcon,
  LockClosedIcon,
  ScaleIcon,
  SparklesIcon
} from '@heroicons/react/24/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { useFrame } from '../engine'
import { anim, lerp, pv, seg, smooth } from '../motion'
import { COLORS, Count, Shape } from '../primitives'
import {
  Envelope,
  Flight,
  Heart,
  MiniPhone,
  Person,
  Walker
} from '../v3/figures'
import { H, U, W, iso, onWallA } from '../v3/iso'
import {
  AnalysisScreen,
  AtsPanel,
  Beam,
  FairPanel,
  GamePanel,
  InterviewCard,
  MagicPanel,
  OptionCards,
  Panel,
  PdfPanel,
  ProfilePanel,
  SupportPanel,
  TalentPhone
} from '../v3/panels'
import { SILHOUETTES, SPOTS, Silhouette, Talent } from '../v4/world'
import { BEATS, atB, inB } from './beats'
import {
  MACHINE_LABEL,
  OfficeWorld5,
  STAFF_BY_ID,
  TALENTS,
  docLanding,
  phoneLanding,
  spawnAt,
  talentTimes
} from './world'

const range = PropTypes.arrayOf(PropTypes.number)

// ---------- Kamera ----------
// f = Fokuspunkt in Welteinheiten, z = Zoom, ox = Versatz in Bildschirmbreiten

const shot = (l, f, z, ox = 0) => ({ l, f, z, ox })
const OVERVIEW = [13, 11, 11]
const FLOOR = [12.2, 17.8, 1.4]
const MONITOR = [10.9, 7.75, 1.4]

const SHOTS = [
  shot(0, OVERVIEW, 0.8),
  shot(atB('intro', 0.6), OVERVIEW, 0.8),
  shot(atB('ohne', 0.08), [15, 8, 1.5], 1.2),
  shot(atB('ohne', 0.95), [15, 8, 1.5], 1.2),
  shot(atB('rewind', 0.5), [13, 10, 3], 0.95),
  shot(atB('rewind', 0.9), [13, 10, 3], 0.95),
  shot(atB('hr', 0.15), [11, 8.5, 3.2], 1.55),
  shot(atB('hr', 0.92), [11, 8.5, 3.2], 1.55),
  shot(atB('analyse', 0.12), [12, 11, 2.4], 1.12),
  shot(atB('analyse', 0.95), [12, 11, 2.4], 1.12),
  shot(atB('profil', 0.15), [9, 6, 2], 1.1, -0.22),
  shot(atB('profil', 0.9), [9, 6, 2], 1.1, -0.22),
  shot(atB('bewerbungen', 0.1), [13.5, 10.5, 1], 0.95),
  shot(atB('bewerbungen', 0.5), [13.5, 10.5, 1], 0.95),
  shot(atB('bewerbungen', 0.62), FLOOR, 1.55),
  shot(atB('bewerbungen', 0.95), FLOOR, 1.55),
  shot(atB('games', 0.12), [9.5, 11.5, 1.5], 1.0, -0.2),
  shot(atB('games', 0.92), [9.5, 11.5, 1.5], 1.0, -0.2),
  shot(atB('magic', 0.14), [5, 4, 5], 2),
  shot(atB('magic', 0.92), [5, 4, 5], 2),
  shot(atB('regeln', 0.12), [12.2, 17.8, 1], 1.45, -0.2),
  shot(atB('regeln', 0.92), [12.2, 17.8, 1], 1.45, -0.2),
  shot(atB('talente', 0.12), [14.6, 18, 1.6], 1.5),
  shot(atB('talente', 0.92), [14.6, 18, 1.6], 1.5),
  shot(atB('report', 0.1), [11.5, 8, 3], 1.25),
  shot(atB('report', 0.28), [11.5, 8, 3], 1.25),
  shot(atB('report', 0.42), [10.5, 10, 2.4], 1.15, -0.22),
  shot(atB('report', 0.92), [10.5, 10, 2.4], 1.15, -0.22),
  shot(atB('kreuz', 0.12), [9, 17, 1.2], 1.35),
  shot(atB('kreuz', 0.92), [9, 17, 1.2], 1.35),
  shot(atB('gespraech', 0.12), [12.2, 9.6, 1.5], 1.9),
  shot(atB('gespraech', 0.95), [12.2, 9.6, 1.5], 1.9),
  shot(atB('team', 0.25), [18, 15, 1], 1.4),
  shot(atB('team', 0.95), [18, 15, 1], 1.4),
  shot(atB('absagen', 0.12), [15, 7, 1], 0.92, -0.23),
  shot(atB('absagen', 0.92), [15, 7, 1], 0.92, -0.23),
  shot(atB('ats', 0.15), MONITOR, 2.2),
  shot(atB('ats', 0.95), MONITOR, 2.2),
  shot(atB('support', 0.2), [11, 8.5, 1.5], 1.8, -0.2),
  shot(atB('support', 0.95), [11, 8.5, 1.5], 1.8, -0.2),
  shot(atB('finale', 0.25), OVERVIEW, 0.8),
  shot(1, OVERVIEW, 0.8)
]

export function camera(l) {
  let i = 1
  while (i < SHOTS.length - 1 && l > SHOTS[i].l) i++
  const a = SHOTS[i - 1]
  const b = SHOTS[i]
  const t = smooth(seg(l, a.l, b.l))
  const pa = iso(...a.f)
  const pb = iso(...b.f)
  return {
    x: lerp(pa.x, pb.x, t),
    y: lerp(pa.y, pb.y, t),
    z: Math.exp(lerp(Math.log(a.z), Math.log(b.z), t)),
    ox: lerp(a.ox, b.ox, t)
  }
}

// ---------- Wege ----------

const rnd = (i, s) => {
  const v = Math.sin(i * 127.1 + s * 311.7) * 43758.5453
  return v - Math.floor(v)
}

const DOOR = iso(SPOTS.door.x, SPOTS.door.y, 2.2)
const MACHINE_TOP = iso(5, 4, 3.4)
const MACHINE_FLOOR = iso(5, 4, 0.4)
const head = (p) => {
  const s = iso(p.x, p.y)
  return { x: s.x, y: s.y - 52 }
}
const MARA = head(SPOTS.mara)
const BOARD_MID = iso(12, 0, 4)

// Auf dem Weg zur Tür: durch den Gang zwischen HR und Customer Success
const exitPoints = (from) => [
  from,
  { x: 16.2, y: 13.2 },
  { x: 16.4, y: 6.2 },
  { x: 22.2, y: 5.4 },
  SPOTS.door
]

const timed = (points, start, step) =>
  points.map((p, i) => ({ at: start + i * step, x: p.x, y: p.y }))

const LEAVERS = TALENTS.filter((t) => t.id !== 'lena' && t.id !== 'jonas')

function talentPath(t) {
  const i = TALENTS.indexOf(t)
  const home = { x: t.x, y: t.y }
  const start = [{ at: spawnAt(i)[0], ...home }]
  if (t.id === 'lena') {
    return [
      ...start,
      { at: atB('report', 0.12), ...home },
      { at: atB('report', 0.3), ...SPOTS.front[0] },
      { at: atB('gespraech', 0.04), ...SPOTS.front[0] },
      { at: atB('gespraech', 0.24), ...SPOTS.interview },
      { at: atB('team', 0.02), ...SPOTS.interview },
      { at: atB('team', 0.14), x: 14.8, y: 13.8 },
      { at: atB('team', 0.28), x: 16.6, y: 19.4 },
      { at: atB('team', 0.4), x: 20.4, y: 18.4 },
      { at: atB('team', 0.5), ...SPOTS.csSeat }
    ]
  }
  if (t.id === 'jonas') {
    return [
      ...start,
      { at: atB('kreuz', 0.15), ...home },
      { at: atB('kreuz', 0.32), x: 8.2, y: 14.2 },
      { at: atB('kreuz', 0.48), x: 6.4, y: 19.6 },
      { at: atB('kreuz', 0.6), ...SPOTS.vSeat }
    ]
  }
  const k = LEAVERS.indexOf(t)
  const exitStart = atB('absagen', 0.12 + k * 0.045)
  const step = atB('absagen', 0.035) - atB('absagen', 0)
  const pos = t.front != null ? SPOTS.front[t.front] : home
  const shortlisted =
    t.front != null
      ? [
          { at: atB('report', 0.16 + t.front * 0.05), ...home },
          { at: atB('report', 0.34 + t.front * 0.05), ...pos }
        ]
      : []
  return [...start, ...shortlisted, ...timed(exitPoints(pos), exitStart, step)]
}

const exitArrival = (path) => {
  const end = path[path.length - 1].at
  return [end - 0.003, end]
}

// ---------- Fliegende Dinge ----------

const PAST_LETTERS = Array.from({ length: 14 }, (_, i) => {
  const desk = iso(10.2 + rnd(i, 1) * 1.6, 7.9 + rnd(i, 2) * 0.6, 1)
  const to = { x: desk.x, y: desk.y - 4 - i * 2.4 }
  return {
    rot: rnd(i, 3) * 30 - 15,
    legs: [
      {
        from: DOOR,
        to,
        at: inB('ohne', 0.04 + i * 0.018, 0.14 + i * 0.018),
        arc: 120 + rnd(i, 4) * 60,
        spin: rnd(i, 5) * 200 - 100
      },
      {
        from: to,
        to: DOOR,
        at: inB('rewind', 0.15 + i * 0.02, 0.45 + i * 0.02),
        arc: 120
      }
    ]
  }
})

const TEMPLATE = [
  {
    legs: [
      {
        from: MACHINE_TOP,
        to: { x: MARA.x, y: MARA.y - 40 },
        at: inB('hr', 0.16, 0.3),
        arc: 80
      }
    ]
  }
]

const EXPERTS = ['csLead', 'csMate', 'vLead']
const KINDS = ['tri', 'sq', 'dot']
const TONES = ['yellow', 'green', 'pink']

const INVITES = EXPERTS.map((id, i) => ({
  legs: [
    {
      from: MARA,
      to: head(STAFF_BY_ID[id]),
      at: inB('analyse', 0.04 + i * 0.04, 0.18 + i * 0.04),
      arc: 70,
      spin: 20
    }
  ]
}))

const ANALYSES = EXPERTS.flatMap((id, i) =>
  [0, 1].map((k) => ({
    kind: KINDS[(i + k) % 3],
    color: TONES[(i + k) % 3],
    legs: [
      {
        from: head(STAFF_BY_ID[id]),
        to: MACHINE_TOP,
        at: inB(
          'analyse',
          0.74 + i * 0.03 + k * 0.02,
          0.88 + i * 0.03 + k * 0.02
        ),
        arc: 90,
        spin: 180
      }
    ]
  }))
)

// Viele Bewerbungen landen auf der Fläche, jede vierte wird gleich ein Mensch
const LETTERS = Array.from({ length: 48 }, (_, i) => {
  const ti = i % 4 === 0 ? i / 4 : null
  const t = TALENTS[ti]
  const pile = t
    ? iso(t.x, t.y)
    : iso(8.8 + rnd(i, 1) * 6.8, 14.8 + rnd(i, 2) * 6)
  const land = { x: pile.x, y: pile.y - 20 }
  const s = i / 48
  const fade = t
    ? [spawnAt(ti)[0] - 0.006, spawnAt(ti)[0] + 0.002]
    : inB('bewerbungen', 0.6 + rnd(i, 6) * 0.26, 0.66 + rnd(i, 6) * 0.26)
  return {
    rot: rnd(i, 3) * 60 - 30,
    legs: [
      {
        from: DOOR,
        to: land,
        at: inB('bewerbungen', 0.04 + s * 0.4, 0.16 + s * 0.4),
        arc: 140 + rnd(i, 4) * 60,
        spin: rnd(i, 5) * 300 - 150
      },
      { from: land, to: { x: land.x, y: land.y - 16 }, at: fade, shrink: true }
    ]
  }
})

const PHONES = TALENTS.map((t, i) => {
  const s = iso(t.x, t.y)
  const land = phoneLanding(i)
  return {
    kind: KINDS[i % 3],
    color: TONES[i % 3],
    legs: [
      {
        from: MACHINE_TOP,
        to: { x: s.x + 18, y: s.y - 44 },
        at: [land - (atB('games', 0.14) - atB('games', 0)), land],
        arc: 110 + (i % 4) * 20,
        spin: 20
      }
    ]
  }
})

const RESULTS = TALENTS.map((t, i) => ({
  kind: KINDS[(i + 1) % 3],
  color: TONES[(i + 2) % 3],
  legs: [
    {
      from: head(t),
      to: MACHINE_TOP,
      at: inB('games', 0.56 + i * 0.015, 0.7 + i * 0.015),
      arc: 120 + (i % 3) * 30,
      spin: 180
    }
  ]
}))

const MAGIC_FLOW = Array.from({ length: 14 }, (_, i) => ({
  kind: KINDS[i % 3],
  color: TONES[(i + 1) % 3],
  legs: [
    {
      from: { x: MACHINE_TOP.x + ((i % 5) - 2) * 14, y: MACHINE_TOP.y },
      to: { x: MACHINE_FLOOR.x + ((i % 3) - 1) * 18, y: MACHINE_FLOOR.y },
      at: inB('magic', 0.16 + i * 0.045, 0.3 + i * 0.045),
      spin: 120
    }
  ]
}))

const PROFILES_OUT = TALENTS.map((t, i) => {
  const s = iso(t.x, t.y)
  const land = docLanding(i)
  return {
    legs: [
      {
        from: MACHINE_TOP,
        to: { x: s.x, y: s.y - 101 },
        at: [land - (atB('talente', 0.14) - atB('talente', 0)), land],
        arc: 110,
        spin: 10
      }
    ]
  }
})

const CARDS = Array.from({ length: 3 }, (_, i) => ({
  legs: [
    {
      from: MACHINE_TOP,
      to: { x: BOARD_MID.x, y: BOARD_MID.y + i * 14 },
      at: inB('report', 0.08 + i * 0.04, 0.22 + i * 0.04),
      arc: 60
    }
  ]
}))

const renderShape = (it) => (
  <g transform='translate(-9 -9)'>
    <Shape kind={it.kind} color={it.color} size={18} />
  </g>
)

const renderLetter = (it) => (
  <g transform={`rotate(${it.rot})`}>
    <Envelope />
  </g>
)

function TemplateDoc() {
  return (
    <g>
      <rect
        x='-14'
        y='-18'
        width='28'
        height='36'
        rx='4'
        fill='#FFFFFF'
        stroke={COLORS.blue}
        strokeWidth='2'
      />
      <rect x='-8' y='-10' width='16' height='3' rx='1.5' fill={COLORS.blue} />
      <rect x='-8' y='-3' width='12' height='3' rx='1.5' fill='#C9D2E6' />
      <rect x='-8' y='4' width='14' height='3' rx='1.5' fill='#C9D2E6' />
    </g>
  )
}

function ProfileDoc() {
  return (
    <g>
      <rect
        x='-9'
        y='-11'
        width='18'
        height='22'
        rx='3'
        fill='#FFFFFF'
        stroke={COLORS.pink}
        strokeWidth='2'
      />
      <circle cx='0' cy='-3' r='3.2' fill={COLORS.pink} />
      <rect x='-5' y='3' width='10' height='2.5' rx='1.2' fill='#C9D2E6' />
    </g>
  )
}

function ShortlistCard() {
  return (
    <g>
      <rect
        x='-22'
        y='-9'
        width='44'
        height='18'
        rx='4'
        fill='#FFFFFF'
        stroke='#C9D2E6'
      />
      <circle cx='-13' cy='0' r='4' fill={COLORS.pink} />
      <rect x='-6' y='-2' width='20' height='4' rx='2' fill='#C9D2E6' />
    </g>
  )
}

// ---------- Overlays in Weltkoordinaten ----------

function Anchor({ at3, dy = 0, children }) {
  const p = iso(...at3)
  return (
    <div className='off-anchor' style={{ left: p.x, top: p.y - dy }}>
      {children}
    </div>
  )
}

Anchor.propTypes = {
  at3: range.isRequired,
  dy: PropTypes.number,
  children: PropTypes.node
}

function Bubble({ at3, dx = 0, dy = 84, show, hide, tone, children }) {
  return (
    <Anchor at3={at3} dy={dy}>
      <div
        className={clsx('off-bubble', tone && `off-bubble--${tone}`)}
        style={{
          marginLeft: dx,
          ...anim({ at: show, out: hide, y: 14, oy: -10, s: 0.7 })
        }}
      >
        {children}
      </div>
    </Anchor>
  )
}

Bubble.propTypes = {
  at3: range.isRequired,
  dx: PropTypes.number,
  dy: PropTypes.number,
  show: range.isRequired,
  hide: range,
  tone: PropTypes.string,
  children: PropTypes.node
}

const SHORTLIST = [
  { name: 'Lena K.', value: 91, color: 'pink' },
  { name: 'Aylin S.', value: 86, color: 'yellow' },
  { name: 'Mo A.', value: 82, color: 'green' }
]

function Board() {
  return (
    <div
      className='off-board'
      style={{
        width: 7 * U,
        height: 3.4 * U,
        transform: onWallA(iso(8.5, 0, 5.7))
      }}
    >
      <div
        className='off-board__idle'
        style={anim({
          at: [-1, 0],
          out: inB('report', 0.18, 0.24),
          y: 0,
          oy: 0
        })}
      >
        <span>Wir suchen</span>
        <strong>Customer Success Manager:in</strong>
      </div>
      <div
        className='off-board__list'
        style={anim({ at: inB('report', 0.22, 0.28), y: 0 })}
      >
        <div className='off-board__head'>Shortlist · Customer Success</div>
        {SHORTLIST.map((s, i) => (
          <div
            key={s.name}
            className={clsx('off-board__row', i === 0 && 'is-top')}
            style={anim({
              at: inB('report', 0.26 + i * 0.04, 0.32 + i * 0.04),
              x: -20,
              y: 0
            })}
          >
            <i style={{ background: COLORS[s.color] }} />
            <span>{s.name}</span>
            <b>{s.value}&nbsp;%</b>
          </div>
        ))}
      </div>
    </div>
  )
}

const QUESTIONS = [
  'Wie wichtig ist Emotionswahrnehmung?',
  'Wie wichtig ist Teamarbeit?',
  'Wie wichtig ist Gewissenhaftigkeit?'
]

function Overlays() {
  const mara = [SPOTS.mara.x, SPOTS.mara.y, 0]
  const staff = (id) => [STAFF_BY_ID[id].x, STAFF_BY_ID[id].y, 0]
  const talent = (id) => {
    const t = TALENTS.find((x) => x.id === id)
    return [t.x, t.y, 0]
  }
  const seat = [SPOTS.csSeat.x, SPOTS.csSeat.y, 0]

  return (
    <div className='off-overlay'>
      <div
        className='off-machine-label'
        style={{
          width: 3 * U,
          transform: onWallA(iso(3.5, 5.5, 2.1)),
          ...MACHINE_LABEL
        }}
      >
        aivy
      </div>
      <Board />

      {/* Freie Plätze */}
      <Anchor at3={seat} dy={40}>
        <div
          className='off-seat'
          style={anim({
            at: inB('ohne', 0.02, 0.06),
            out: inB('ohne', 0.54, 0.6),
            s: 0.3,
            y: 10
          })}
        >
          ?
        </div>
      </Anchor>
      <Anchor at3={seat} dy={40}>
        <div
          className='off-seat'
          style={anim({
            at: inB('ohne', 0.92, 0.97),
            out: inB('bewerbungen', 0.02, 0.08),
            s: 0.3,
            y: 10
          })}
        >
          ?
        </div>
      </Anchor>
      <Anchor at3={seat} dy={92}>
        <Silhouette
          shapes={SILHOUETTES.cs}
          label='Customer Success'
          at={inB('bewerbungen', 0.02, 0.1)}
          fillAt={inB('team', 0.5, 0.58)}
        />
      </Anchor>
      <Anchor at3={[SPOTS.vSeat.x, SPOTS.vSeat.y, 0]} dy={92}>
        <Silhouette
          shapes={SILHOUETTES.v}
          label='Vertrieb'
          at={inB('bewerbungen', 0.04, 0.12)}
          fillAt={inB('kreuz', 0.6, 0.66)}
        />
      </Anchor>

      {/* Ohne Aivy */}
      <Bubble
        at3={staff('csLead')}
        show={inB('ohne', 0.04, 0.09)}
        hide={inB('ohne', 0.2, 0.25)}
        tone='green'
      >
        Wir brauchen dringend Verstärkung!
      </Bubble>
      <Bubble
        at3={mara}
        show={inB('ohne', 0.34, 0.4)}
        hide={inB('ohne', 0.5, 0.55)}
        tone='question'
      >
        <small>Viel Arbeit, wenig Einblick</small>
        Ich lese jeden Lebenslauf. Aber wer passt wirklich ins Team?
      </Bubble>
      <Bubble
        at3={seat}
        dx={60}
        show={inB('ohne', 0.68, 0.73)}
        hide={inB('ohne', 0.8, 0.85)}
      >
        Hm, das ist irgendwie nicht meins.
      </Bubble>
      <Bubble
        at3={staff('csLead')}
        show={inB('ohne', 0.9, 0.95)}
        hide={inB('rewind', 0.1, 0.2)}
      >
        Schon wieder jemand weg?
      </Bubble>

      {/* HR startet mit Aivy */}
      <Bubble
        at3={mara}
        show={inB('hr', 0.04, 0.12)}
        hide={inB('hr', 0.16, 0.22)}
        tone='blue'
      >
        Diesmal hole ich mir Aivy für die Vorauswahl dazu.
      </Bubble>
      <Anchor at3={mara} dy={86}>
        <div
          style={anim({
            at: inB('hr', 0.2, 0.24),
            out: inB('hr', 0.9, 1),
            y: 0
          })}
        >
          <OptionCards />
        </div>
      </Anchor>

      {/* Anforderungsanalyse */}
      {EXPERTS.map((id, i) => (
        <Anchor key={id} at3={staff(id)} dy={86}>
          <div style={{ marginLeft: [-70, 80, 0][i] }}>
            <AnalysisScreen delay={i * 0.04} question={QUESTIONS[i]} />
          </div>
        </Anchor>
      ))}
      <Anchor at3={mara} dy={86}>
        <div
          className='off-tag off-tag--green'
          style={anim({
            at: inB('analyse', 0.42, 0.52),
            out: inB('analyse', 0.88, 0.96),
            y: 10,
            s: 0.6
          })}
        >
          <CheckIcon width={13} height={13} /> Kein Aufwand für HR
        </div>
      </Anchor>
      <Anchor at3={[5, 4, 3.4]} dy={60}>
        <div
          className='off-tag off-tag--blue'
          style={anim({
            at: inB('analyse', 0.88, 0.96),
            out: inB('profil', 0.1, 0.2),
            y: 10,
            s: 0.6
          })}
        >
          <CheckIcon width={13} height={13} /> Expert:innen stimmen überein
        </div>
      </Anchor>

      {/* Bewerbungen */}
      <Anchor at3={[SPOTS.door.x, SPOTS.door.y, 4.6]} dy={10}>
        <div
          className='off-counter'
          style={anim({
            at: inB('bewerbungen', 0.06, 0.12),
            out: inB('bewerbungen', 0.52, 0.58),
            y: 12,
            s: 0.7
          })}
        >
          <Count
            to={128}
            at={inB('bewerbungen', 0.06, 0.5)}
            className='off-counter__num'
          />
          <span>Bewerbungen</span>
        </div>
      </Anchor>

      {/* Game-based Assessment */}
      <Anchor at3={[5, 4, 3.4]} dy={70}>
        <div
          className='off-counter off-counter--blue'
          style={anim({
            at: inB('games', 0.08, 0.16),
            out: inB('games', 0.88, 0.96),
            y: 12,
            s: 0.7
          })}
        >
          <span>Game-based Assessment</span>
          <small>an alle Bewerbenden</small>
        </div>
      </Anchor>

      {/* Blick in Aivy */}
      <Anchor at3={[5, 4, 3.4]} dy={40}>
        <MagicPanel />
      </Anchor>

      {/* Bewerbende lieben es */}
      <Anchor at3={[20.4, 21, 1]}>
        <TalentPhone />
      </Anchor>
      <Bubble
        at3={talent('t0')}
        dy={128}
        show={inB('talente', 0.42, 0.5)}
        hide={inB('talente', 0.9, 1)}
        tone='pink'
      >
        Hat richtig Spaß gemacht!
      </Bubble>
      <Bubble
        at3={talent('t6')}
        dy={128}
        show={inB('talente', 0.54, 0.62)}
        hide={inB('talente', 0.9, 1)}
        tone='yellow'
      >
        Endlich mal kein langweiliger Fragebogen.
      </Bubble>

      {/* Kreuzmatching */}
      <Bubble
        at3={[14.8, 17.6, 0]}
        dy={128}
        show={inB('kreuz', 0.04, 0.12)}
        hide={inB('kreuz', 0.22, 0.28)}
        tone='blue'
      >
        Kreuzmatching: Jonas passt in den Vertrieb.
      </Bubble>
      <Bubble
        at3={staff('vLead')}
        show={inB('kreuz', 0.66, 0.74)}
        hide={inB('kreuz', 0.92, 1)}
        tone='green'
      >
        Willkommen, Jonas!
      </Bubble>

      {/* Gespräch */}
      <Anchor at3={mara} dy={96}>
        <div style={{ marginLeft: -60 }}>
          <InterviewCard />
        </div>
      </Anchor>
      <Bubble
        at3={[SPOTS.interview.x, SPOTS.interview.y, 0]}
        dx={50}
        show={inB('gespraech', 0.66, 0.72)}
        hide={inB('gespraech', 0.9, 1)}
        tone='pink'
      >
        Gern, da gab es einen Fall …
      </Bubble>

      {/* Team */}
      <Bubble
        at3={staff('csLead')}
        show={inB('team', 0.58, 0.66)}
        hide={inB('team', 0.92, 1)}
        tone='green'
      >
        Willkommen im Team, Lena!
      </Bubble>

      {/* Faire Absagen */}
      <Bubble
        at3={[SPOTS.door.x - 1.2, SPOTS.door.y, 4.6]}
        dx={-90}
        dy={0}
        show={inB('absagen', 0.42, 0.5)}
        hide={inB('absagen', 0.9, 1)}
        tone='pink'
      >
        Danke für mein Stärkenprofil!
      </Bubble>
    </div>
  )
}

// ---------- Regelbasiert ----------

function RulesPanel() {
  return (
    <Panel
      at={inB('regeln', 0.06, 0.2)}
      out={inB('regeln', 0.9, 1)}
      className='off-doc hum-rules'
    >
      <div className='off-rules__badge'>
        <LockClosedIcon width={16} height={16} /> Im Kern ohne KI
      </div>
      <div className='off-doc__title kom-rules__title'>
        Regelbasiert. Nachvollziehbar. <span>AI&#8209;Act&#8209;ready.</span>
      </div>
      <p className='off-doc__lead'>
        Aivy gleicht jede Person nach denselben festen Regeln mit dem
        Anforderungsprofil ab. Keine Blackbox bewertet Menschen, jedes Ergebnis
        lässt sich bis auf die einzelne Dimension erklären.
      </p>
      <div className='hum-rules__beam'>
        <Beam ideal={[56, 82]} trans={[44, 92]} value={70} />
        <div className='off-legend'>
          <span>
            <i style={{ background: 'rgb(167 247 231)' }} /> Idealbereich
          </span>
          <span>
            <i style={{ background: 'rgb(253 218 148)' }} /> Übergangsbereich
          </span>
        </div>
      </div>
      <div className='hum-rules__rings'>
        <span>
          <b style={{ borderColor: COLORS.green }} /> passt zu Customer Success
        </span>
        <span>
          <b style={{ borderColor: COLORS.blue }} /> passt zum Vertrieb
        </span>
      </div>
      <div className='hum-rules__chips'>
        <span className='off-chip off-chip--blue'>
          <ScaleIcon width={12} height={12} /> Gleiche Kriterien für alle
        </span>
        <span className='off-chip'>
          <SparklesIcon width={12} height={12} /> KI nur zum Erklären
        </span>
      </div>
    </Panel>
  )
}

// ---------- Vergleich am Ende ----------

const COMPARE = [
  ['Lebenslauf', 'Der Mensch dahinter'],
  ['Viel Handarbeit', 'Vorauswahl mit Aivy'],
  ['Fehlbesetzung', 'Passung, die bleibt'],
  ['Absage ohne Feedback', 'Stärkenprofil für alle'],
  ['Talent verloren', 'Kreuzmatching']
]

// ---------- Bühne ----------

const DEFAULT_TITLE = 'Finde den Menschen hinter dem Lebenslauf.'
const DEFAULT_LEAD =
  'Eine Geschichte in zwei Anläufen, erst ohne Aivy, dann mit. Von der offenen Stelle bis zum neuen Teammitglied.'

/**
 * `worldExtras` hängt in der Welt (zoomt mit der Kamera), `screenExtras`
 * liegt auf Bildschirmebene. Version 6 nutzt beides für ihre Exkurse.
 * `cameraOverride` liefert optional eine eigene Kamera, solange Version 7
 * ein Extra zeigt, `hideNav` blendet die Kapitelnavigation aus.
 * `textLayer(BEATS)` ersetzt die Captions unten links (Version 8).
 */
export default function KombiStory({
  title = DEFAULT_TITLE,
  lead = DEFAULT_LEAD,
  worldExtras,
  screenExtras,
  cameraOverride,
  hideNav = false,
  textLayer,
  centerY = 0.48
}) {
  const stage = useRef(null)
  const world = useRef(null)
  const last = useRef(0)
  const [beat, setBeat] = useState(0)

  const override = useRef(cameraOverride)
  override.current = cameraOverride
  const center = useRef(centerY)
  center.current = centerY

  const place = (l) => {
    const host = stage.current
    if (!host || !world.current) return
    // Schmale Screens: näher ran, sonst werden die Figuren winzig
    const narrow = host.clientWidth < 700
    const fit =
      Math.min(host.clientWidth / W, host.clientHeight / H) *
      (narrow ? 1.9 : 1.08)
    const cam = override.current?.() || camera(l)
    const s = fit * cam.z
    const cx = host.clientWidth * (0.5 + (narrow ? 0 : cam.ox))
    const cy = host.clientHeight * (narrow ? 0.4 : center.current)
    world.current.style.transform = `translate(${cx - cam.x * s}px, ${cy - cam.y * s}px) scale(${s})`
  }

  useFrame((l) => {
    last.current = l
    place(l)
    const i = BEATS.findIndex((b) => l >= b.at[0] && l < b.at[1])
    setBeat(i === -1 ? BEATS.length - 1 : i)
  })

  useEffect(() => {
    const ro = new ResizeObserver(() => place(last.current))
    if (stage.current) ro.observe(stage.current)
    return () => ro.disconnect()
  }, [])

  const jump = (i) => {
    const section = stage.current?.closest('.off-section')
    if (!section) return
    const top = section.getBoundingClientRect().top + window.scrollY
    const [s, e] = BEATS[i].at
    const target =
      top + (s + (e - s) * 0.5) * (section.offsetHeight - window.innerHeight)
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({
      top: i === 0 ? top : target,
      behavior: reduce ? 'auto' : 'smooth'
    })
  }

  const toTop = () => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' })
  }

  const maxPath = [
    { at: atB('ohne', 0.42), ...SPOTS.door },
    { at: atB('ohne', 0.48), x: 22.2, y: 5.6 },
    { at: atB('ohne', 0.54), x: 20.4, y: 18.4 },
    { at: atB('ohne', 0.6), ...SPOTS.csSeat },
    { at: atB('ohne', 0.82), ...SPOTS.csSeat },
    { at: atB('ohne', 0.86), x: 20.4, y: 18.4 },
    { at: atB('ohne', 0.92), x: 22.2, y: 5.6 },
    { at: atB('ohne', 0.97), ...SPOTS.door }
  ]
  const maraAt = iso(SPOTS.mara.x, SPOTS.mara.y)

  return (
    <div ref={stage} className='off-stage'>
      <div
        ref={world}
        className='off-world'
        style={{
          width: W,
          height: H,
          ...pv('ga', inB('ohne', 0, 0.05)),
          ...pv('gb', inB('rewind', 0.35, 0.85)),
          filter: 'grayscale(calc(var(--ga) * (1 - var(--gb)) * 0.9))'
        }}
      >
        <svg
          width={W}
          height={H}
          viewBox={`0 0 ${W} ${H}`}
          className='off-svg'
          aria-hidden='true'
        >
          <OfficeWorld5 />

          <Flight items={PAST_LETTERS} render={renderLetter} />
          <Flight items={TEMPLATE} render={() => <TemplateDoc />} />
          <Flight items={INVITES} render={() => <Envelope />} />
          <Flight items={ANALYSES} render={renderShape} />
          <Flight items={LETTERS} render={renderLetter} />
          <Flight items={MAGIC_FLOW} render={renderShape} />
          <Flight items={CARDS} render={() => <ShortlistCard />} />

          {/* Mara bleibt am Schreibtisch */}
          <g
            className='kom-mara'
            transform={`translate(${maraAt.x} ${maraAt.y})`}
          >
            <Person
              body='blue'
              skin='#E0AC69'
              hair='#5A3825'
              happy={inB('analyse', 0.46, 0.56)}
              sad={[...inB('ohne', 0.86, 0.92), ...inB('rewind', 0.3, 0.6)]}
              sweat={[...inB('ohne', 0.16, 0.24), ...inB('ohne', 0.4, 0.46)]}
            />
            <Heart
              x={-12}
              y={-80}
              at={inB('gespraech', 0.76, 0.82)}
              out={inB('gespraech', 0.94, 1)}
            />
            <Heart x={0} y={-78} at={inB('finale', 0.1, 0.25)} />
          </g>

          {/* Max, die Fehlbesetzung aus der Rückblende */}
          <g
            style={anim({
              at: inB('ohne', 0.4, 0.43),
              out: inB('ohne', 0.95, 0.99),
              y: 0,
              oy: 0
            })}
          >
            <Walker path={maxPath}>
              <Person
                body='#9AA5B8'
                skin='#F2C9A5'
                hair='#5A3825'
                happy={[2, 3]}
                sad={[...inB('ohne', 0.66, 0.72), 2, 3]}
              />
            </Walker>
          </g>

          {TALENTS.map((t, i) => {
            const path = talentPath(t)
            const leaves = LEAVERS.includes(t)
            return (
              <Walker key={t.id} path={path}>
                <Talent
                  talent={t}
                  spawn={spawnAt(i)}
                  exitAt={leaves ? exitArrival(path) : undefined}
                  times={talentTimes(t, i)}
                />
                {t.id === 'lena' && (
                  <>
                    <Heart
                      x={12}
                      y={-130}
                      at={inB('gespraech', 0.78, 0.84)}
                      out={inB('gespraech', 0.94, 1)}
                    />
                    <Heart x={0} y={-130} at={inB('finale', 0.1, 0.25)} />
                  </>
                )}
                {t.id === 'jonas' && (
                  <Heart x={0} y={-130} at={inB('finale', 0.1, 0.25)} />
                )}
              </Walker>
            )
          })}

          {/* Über den Figuren, damit nichts verdeckt wird */}
          <Flight
            items={PHONES}
            render={(it) => <MiniPhone shape={it.kind} color={it.color} />}
          />
          <Flight items={RESULTS} render={renderShape} />
          <Flight items={PROFILES_OUT} render={() => <ProfileDoc />} />

          {['csLead', 'csMate', 'csNew'].map((id, i) => {
            const s = iso(STAFF_BY_ID[id].x, STAFF_BY_ID[id].y)
            return (
              <Heart
                key={id}
                x={s.x + (i - 1) * 4}
                y={s.y - 80}
                at={inB('team', 0.62 + i * 0.05, 0.72 + i * 0.05)}
                out={inB('team', 0.94, 1)}
              />
            )
          })}
        </svg>

        <Overlays />
        {worldExtras}
      </div>

      {/* Bildschirm-Ebene */}
      <nav className='off-nav' aria-label='Kapitel' hidden={hideNav}>
        {BEATS.map((b, i) => (
          <button
            key={b.id}
            type='button'
            className={clsx('off-nav__dot', i === beat && 'is-active')}
            onClick={() => jump(i)}
            aria-label={b.label}
            title={b.label}
          />
        ))}
        <span className='off-nav__label'>{BEATS[beat].label}</span>
      </nav>

      <div
        className='off-intro'
        style={anim({ at: [-1, 0], out: inB('intro', 0.6, 1), oy: -40 })}
      >
        <div className='off-eyebrow'>Recruiting mit Aivy</div>
        <h2 className='off-title'>{title}</h2>
        <p>{lead}</p>
        <div className='off-cue'>
          <ArrowDownIcon width={16} height={16} /> Scroll los
        </div>
      </div>

      <div
        className='hum-tag'
        style={anim({
          at: inB('ohne', 0.02, 0.08),
          out: inB('rewind', 0.3, 0.5),
          y: -10,
          oy: -10
        })}
      >
        Rückblende · ohne Aivy
      </div>

      <div
        className='hum-cost'
        style={anim({
          at: inB('ohne', 0.9, 0.96),
          out: inB('rewind', 0.12, 0.24),
          y: -20,
          oy: -20
        })}
      >
        <span>Zeit verloren</span>
        <span>Budget verloren</span>
        <span>Team frustriert</span>
      </div>

      <div
        className='hum-rewind'
        style={anim({
          at: inB('rewind', 0.08, 0.24),
          out: inB('rewind', 0.82, 0.98),
          s: 0.8,
          y: 20
        })}
      >
        <span className='hum-rewind__icon'>
          <BackwardIcon width={34} height={34} />
        </span>
        <strong>Nochmal. Diesmal mit Aivy.</strong>
      </div>

      <div
        className='hum-headline'
        hidden={!!textLayer}
        style={anim({
          at: inB('bewerbungen', 0.62, 0.72),
          out: inB('bewerbungen', 0.92, 1),
          y: 30,
          oy: -20
        })}
      >
        Aus Lebensläufen werden Menschen.
      </div>

      <ProfilePanel />
      <GamePanel />
      <RulesPanel />
      <PdfPanel />
      <FairPanel />
      <AtsPanel />
      <SupportPanel />

      {textLayer?.(BEATS)}
      {BEATS.filter((b) => b.text && !textLayer).map((b) => {
        const i = BEATS.indexOf(b)
        const len = b.at[1] - b.at[0]
        return (
          <div
            key={b.id}
            className='off-caption'
            style={anim({
              at: [b.at[0] + len * 0.04, b.at[0] + len * 0.14],
              out: [b.at[1] - len * 0.08, b.at[1]],
              y: 24,
              oy: -16
            })}
          >
            <div className='off-eyebrow'>
              {i} · {b.label}
            </div>
            <p>{b.text}</p>
          </div>
        )
      })}

      <div
        className='hum-compare'
        style={anim({ at: inB('finale', 0.3, 0.5), y: 30 })}
      >
        <h2 className='off-title'>
          Alle happy. Auch die, die nicht genommen wurden.
        </h2>
        <div className='hum-compare__grid'>
          <span className='hum-compare__head'>Ohne Aivy</span>
          <span />
          <span className='hum-compare__head hum-compare__head--on'>
            Mit Aivy
          </span>
          {COMPARE.map(([a, b], i) => (
            <div
              key={a}
              className='hum-compare__row'
              style={anim({
                at: inB('finale', 0.4 + i * 0.06, 0.5 + i * 0.06),
                y: 12
              })}
            >
              <span className='hum-compare__old'>{a}</span>
              <span className='hum-compare__arrow'>→</span>
              <span className='hum-compare__new'>{b}</span>
            </div>
          ))}
        </div>
        <div className='off-outro__actions'>
          <Link to='/career/new' className='off-cta'>
            Stelle anlegen
          </Link>
          <button
            type='button'
            onClick={toTop}
            className='off-cta off-cta--ghost'
          >
            Nochmal von vorn
          </button>
        </div>
      </div>
      {screenExtras}
    </div>
  )
}

KombiStory.propTypes = {
  title: PropTypes.string,
  lead: PropTypes.string,
  worldExtras: PropTypes.node,
  screenExtras: PropTypes.node,
  cameraOverride: PropTypes.func,
  hideNav: PropTypes.bool,
  textLayer: PropTypes.func,
  centerY: PropTypes.number
}
